import {
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as PostIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

import {
  GeneralTableBox,
  HeadTableCell,
  NoDataSkeleton,
  PaginationTable,
  RowTableCell,
  StatusChip,
  TableBodySkeleton,
} from '../../../common/components';
import { formatCurrency, formatDate } from '../../../utils/formatUtils';
import { ACCOUNTING_STATUS_CONFIG } from '../constants/accountingEntriesConstants';
import {
  getAccountingOriginLabel,
  humanizeTechnicalValue,
  roundMoney,
  sumAccountingLines,
} from '../utils/accountingEntriesUtils';

const AccountingEntriesTable = ({
  entries,
  isLoading,
  pagination,
  onView,
  onEdit,
  onPost,
  onCancel,
}) => (
  <>
    <GeneralTableBox>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <HeadTableCell>Número</HeadTableCell>
            <HeadTableCell>Fecha</HeadTableCell>
            <HeadTableCell>Origen</HeadTableCell>
            <HeadTableCell>Descripción</HeadTableCell>
            <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Debe</HeadTableCell>
            <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Haber</HeadTableCell>
            <HeadTableCell>Estado</HeadTableCell>
            <HeadTableCell align="center">Acciones</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? <TableBodySkeleton /> : entries.length ? pagination.paginatedRows.map((entry) => {
            const statusConfig = ACCOUNTING_STATUS_CONFIG[entry.status] || {
              label: humanizeTechnicalValue(entry.status),
              color: 'default',
            };
            const totals = sumAccountingLines(entry.lines || []);
            const canEdit = entry.source_type === 'manual' && entry.status === 'draft';
            const canPost = entry.status === 'draft' && roundMoney(totals.debit - totals.credit) === 0;
            const canCancel = entry.status !== 'cancelled';

            return (
              <TableRow key={entry.uuid} hover>
                <RowTableCell>{entry.entry_number}</RowTableCell>
                <RowTableCell>{formatDate(entry.entry_date)}</RowTableCell>
                <RowTableCell>{getAccountingOriginLabel(entry.source_type)}</RowTableCell>
                <RowTableCell>{entry.description}</RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>
                  {formatCurrency(totals.debit, 2)}
                </RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>
                  {formatCurrency(totals.credit, 2)}
                </RowTableCell>
                <RowTableCell>
                  <StatusChip size="small" color={statusConfig.color} label={statusConfig.label} />
                </RowTableCell>
                <RowTableCell align="center">
                  <Tooltip title="Ver detalle">
                    <IconButton size="small" color="primary" onClick={() => onView(entry)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={canEdit ? 'Editar asiento manual' : 'Solo se editan manuales en borrador'}>
                    <span>
                      <IconButton size="small" color="primary" onClick={() => onEdit(entry)} disabled={!canEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={canPost ? 'Contabilizar' : 'Solo borradores balanceados'}>
                    <span>
                      <IconButton size="small" color="success" onClick={() => onPost(entry)} disabled={!canPost}>
                        <PostIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={canCancel ? 'Anular' : 'Ya está anulado'}>
                    <span>
                      <IconButton size="small" color="error" onClick={() => onCancel(entry)} disabled={!canCancel}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </RowTableCell>
              </TableRow>
            );
          }) : (
            <NoDataSkeleton colSpan={8} message="No hay asientos contables cargados." />
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>

    {!!entries.length && (
      <PaginationTable
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        rowsPerPage={pagination.rowsPerPage}
        onPageChange={pagination.handlePageChange}
        onRowsPerPageChange={pagination.handleRowsPerPageChange}
      />
    )}
  </>
);

export default AccountingEntriesTable;
